/**
 * Shared structured-address picker for the composer post types.
 *
 * Renders a text input with a Google Places autocomplete dropdown.
 * On select, fetches place details and emits a structured object that
 * matches the backend's expected shape (see
 * sharpenn-backend/lib/endpoints/_shared/validators/structured-address.js):
 *
 *   {
 *     placeId, line1, city, state, postalCode, country,
 *     lat, lng, formatted
 *   }
 *
 * `formatted` is the human-readable line — composers send it back to the
 * backend's legacy `location` column as a display fallback during cutover.
 *
 * Lazy-loads the Maps script via the shared `utils/googleMaps`
 * helper, so every picker on the page shares one script tag and one
 * env var (REACT_APP_GOOGLE_API_ID). If the script fails to load
 * (no key, offline, blocked) the input degrades to a plain text field
 * — the user sees a sensible placeholder and the composer can still
 * be filled in for a draft, but submit will fail backend validation
 * for active listings, which is the right behavior.
 */

import {useEffect, useRef, useState} from 'react'
import {ensureGoogleMaps} from 'utils/googleMaps'

const findComponent = (components, type, useShortName = false) => {
  if (!Array.isArray(components)) return null
  const match = components.find(
    (c) => Array.isArray(c.types) && c.types.includes(type)
  )
  if (!match) return null
  return useShortName ? match.short_name : match.long_name
}

/**
 * Convert a Google Places `getDetails` response into our structured-address
 * shape. Returns null if the place is unusable (no geometry, no place_id).
 */
const placeToAddress = (place) => {
  const components = place?.address_components || []
  const lat = place?.geometry?.location?.lat?.()
  const lng = place?.geometry?.location?.lng?.()
  if (!place?.place_id || typeof lat !== 'number' || typeof lng !== 'number') {
    return null
  }
  const streetNumber = findComponent(components, 'street_number')
  const route = findComponent(components, 'route')
  const line1 =
    [streetNumber, route].filter(Boolean).join(' ').trim() || place.name || ''
  const city =
    findComponent(components, 'locality') ||
    findComponent(components, 'sublocality_level_1') ||
    findComponent(components, 'postal_town') ||
    ''
  // short_name on state so we get "AZ" rather than "Arizona" — matches
  // common US listings convention.
  const state =
    findComponent(components, 'administrative_area_level_1', true) || ''
  const postalCode = findComponent(components, 'postal_code') || null
  const country = findComponent(components, 'country') || ''
  return {
    placeId: place.place_id,
    line1,
    city,
    state,
    postalCode,
    country,
    lat,
    lng,
    formatted: place.formatted_address || place.name || line1,
  }
}

const AddressAutocomplete = ({
  value,
  onChange,
  placeholder = 'Start typing an address…',
  disabled = false,
  autoFocus = false,
  className = '',
}) => {
  const inputRef = useRef(null)
  const sessionTokenRef = useRef(null)
  const debounceRef = useRef(null)
  const [text, setText] = useState(value?.formatted || '')
  const [predictions, setPredictions] = useState([])
  const [open, setOpen] = useState(false)

  // Keep the displayed text in sync if the parent clears or replaces value.
  useEffect(() => {
    setText(value?.formatted || '')
  }, [value])

  useEffect(() => {
    ensureGoogleMaps()
  }, [])

  const fetchPredictions = (input) => {
    if (!input || !window.google?.maps?.places) {
      setPredictions([])
      setOpen(false)
      return
    }
    const service = new window.google.maps.places.AutocompleteService()
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken()
    }
    service.getPlacePredictions(
      {
        input,
        sessionToken: sessionTokenRef.current,
        types: ['geocode', 'establishment'],
      },
      (results, status) => {
        const ok = window.google.maps.places.PlacesServiceStatus.OK
        if (status === ok && results) {
          setPredictions(results)
          setOpen(true)
        } else {
          setPredictions([])
          setOpen(false)
        }
      }
    )
  }

  const handleChange = (e) => {
    const next = e.target.value
    setText(next)
    // Typing invalidates the previously-selected place — clear the
    // structured value so a stale placeId can't be submitted alongside
    // text the user is editing.
    if (value) onChange(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchPredictions(next.trim()), 250)
  }

  const handleSelect = (prediction) => {
    if (!window.google?.maps?.places) return
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    )
    placesService.getDetails(
      {
        placeId: prediction.place_id,
        sessionToken: sessionTokenRef.current,
        fields: [
          'place_id',
          'formatted_address',
          'address_components',
          'geometry',
          'name',
        ],
      },
      (details, status) => {
        const ok = window.google.maps.places.PlacesServiceStatus.OK
        if (status !== ok || !details) return
        const address = placeToAddress(details)
        // Drop the session token after a successful selection — the next
        // typing pass starts a fresh billing session.
        sessionTokenRef.current = null
        if (!address) return
        setText(address.formatted)
        setOpen(false)
        setPredictions([])
        onChange(address)
      }
    )
  }

  const handleClear = () => {
    setText('')
    setPredictions([])
    setOpen(false)
    onChange(null)
    inputRef.current?.focus()
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={handleChange}
        onFocus={() => predictions.length > 0 && setOpen(true)}
        onBlur={() => {
          // Delay close so click on a prediction still registers.
          setTimeout(() => setOpen(false), 150)
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={className}
        autoComplete="off"
      />
      {text && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear address"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-green-gray hover:text-sharpenn-black w-6 h-6 flex items-center justify-center"
        >
          ×
        </button>
      )}
      {open && predictions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-sharpenn-blush/40 rounded-lg shadow-md max-h-64 overflow-auto">
          {predictions.map((p) => (
            <li
              key={p.place_id}
              onMouseDown={(e) => {
                // Use mousedown so we beat the input's blur-driven close.
                e.preventDefault()
                handleSelect(p)
              }}
              className="px-3 py-2 text-sm text-sharpenn-black cursor-pointer hover:bg-sharpenn-cream"
            >
              <div className="font-medium">
                {p.structured_formatting?.main_text || p.description}
              </div>
              {p.structured_formatting?.secondary_text && (
                <div className="text-xs text-green-gray">
                  {p.structured_formatting.secondary_text}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default AddressAutocomplete
